"use client";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

/**
 * ProgramFee2 — Variant 3
 * Light / maroon "Program Fees" design from Figma "Typography System · 4 Breakpoints".
 * Adds a recursive, N-level tab system on top of the existing flat `categories`: each
 * category carries an optional `tab_group` ("/"-separated path) that places it under
 * one or more OUTER tabs (e.g. "Indian Student"); the category titles remain the inner
 * pills (Total Lumpsum / Annual / Semester-wise). With no `tab_group`, it degrades to a
 * single pill level (current behaviour). variant_1 is rendered by the parent component.
 */

const MAROON = "#9f1c34";
const INK = "#101112";
const MUTED = "rgba(0,0,0,0.7)";
const FONT_POPPINS = "var(--font-poppins)";
const FONT_VISUAL = "var(--font-wf-visual-sans)";
/** Variant 3's fall-off colour for the fee table, used when `table_gradient_to` is unset/still on the v1 default. */
const TABLE_GRADIENT_END = "#000000";

/**
 * Variant 3 shares its DB row (and therefore its defaults) with variant 1's blue/teal palette,
 * so a page switched to this variant without touching the colours would render off-theme.
 * Values still sitting on a v1 default are mapped to the variant 3 equivalent; anything the CMS
 * user actually picked is passed through. Compared case-insensitively — the colour picker may
 * emit `#342B7C` for the same default stored as `#342b7c`.
 */
const themed = (value: string | undefined | null, v1Defaults: string[], v2Default: string): string => {
  if (!value) return v2Default;
  const normalized = value.trim().toLowerCase();
  return v1Defaults.some((d) => d.trim().toLowerCase() === normalized) ? v2Default : value;
};

type Rgba = { r: number; g: number; b: number; a: number };

/** Parse the formats a CMS COLOR field emits: #rgb / #rgba / #rrggbb / #rrggbbaa and rgb()/rgba(). */
const parseColor = (input: string | undefined | null): Rgba | null => {
  const value = String(input ?? "").trim();
  if (!value) return null;

  if (value.startsWith("#")) {
    const raw = value.slice(1);
    const full =
      raw.length === 3 || raw.length === 4
        ? raw
            .split("")
            .map((c) => c + c)
            .join("")
        : raw.length === 6 || raw.length === 8
          ? raw
          : null;
    if (!full) return null;
    const parts = [full.slice(0, 2), full.slice(2, 4), full.slice(4, 6), full.slice(6, 8) || "ff"].map((h) =>
      Number.parseInt(h, 16),
    );
    if (parts.some((n) => Number.isNaN(n))) return null;
    return { r: parts[0], g: parts[1], b: parts[2], a: parts[3] / 255 };
  }

  const fn = value.match(/^rgba?\(([^)]+)\)$/i);
  if (!fn) return null;
  const parts = fn[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3) return null;
  const channel = (p: string) => (p.endsWith("%") ? (Number.parseFloat(p) * 255) / 100 : Number.parseFloat(p));
  const [r, g, b] = parts.slice(0, 3).map(channel);
  const rawAlpha = parts[3];
  const a =
    rawAlpha === undefined
      ? 1
      : rawAlpha.endsWith("%")
        ? Number.parseFloat(rawAlpha) / 100
        : Number.parseFloat(rawAlpha);
  if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
  return { r, g, b, a };
};

/** Re-alpha a CMS colour for the section background stops (mirrors the Faculty2/About2 overlay recipe). */
const withAlpha = (value: string | undefined | null, alpha: number): string => {
  const parsed = parseColor(value);
  if (!parsed) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${Math.round(parsed.r)}, ${Math.round(parsed.g)}, ${Math.round(parsed.b)}, ${alpha})`;
};

/**
 * `backvariant` and `gradient_color_1..4` are shared with variant 1 and default to its dark
 * teal/black/purple palette, while `backvariant` itself defaults to "variant_1". Applying that
 * unconditionally would lay a dark gradient over every existing light/maroon page, so the overlay
 * only engages once an editor has actually moved one of the four colours off its v1 default —
 * the same "untouched default" contract `themed()` uses. Until then the section stays flat.
 */
const V1_GRADIENT_DEFAULTS = ["#00d4eb54", "#000000a8", "#000000a8", "#5345c3"];

const isUntouched = (value: string | undefined | null, v1Default: string): boolean => {
  const raw = String(value ?? "").trim().toLowerCase();
  return raw === "" || raw === v1Default;
};

/** Opacity ramp + stop positions come from the shared Faculty2/About2/Faqs2 background recipe. */
const buildSectionGradient = (
  backvariant: string | undefined | null,
  colors: (string | undefined | null)[],
): string | undefined => {
  if (colors.every((c, i) => isUntouched(c, V1_GRADIENT_DEFAULTS[i]))) return undefined;
  if (backvariant === "variant_3") return undefined; // "Solid Color" — background_color alone

  const stops = [
    `${withAlpha(colors[0], 0.33)} 0%`,
    `${withAlpha(colors[1], 0.66)} 24.68%`,
    `${withAlpha(colors[2], 0.66)} 79.09%`,
    `${withAlpha(colors[3], 0.46)} 100%`,
  ].join(", ");

  // variant_2 = "Angled Gradient", variant_1 (and anything unrecognised) = "Diagonal Gradient".
  return `linear-gradient(${backvariant === "variant_2" ? 292 : 253}deg, ${stops})`;
};

/** Linear sRGB mix; `amount` is how far from `from` towards `to` (0 → from, 1 → to). */
const mixColor = (from: Rgba, to: Rgba, amount: number): string => {
  const channel = (a: number, b: number) => Math.round(a + (b - a) * amount);
  const alpha = Number((from.a + (to.a - from.a) * amount).toFixed(3));
  return `rgba(${channel(from.r, to.r)}, ${channel(from.g, to.g)}, ${channel(from.b, to.b)}, ${alpha})`;
};

/**
 * Figma's maroon radial table background: the "from" colour glows near the top-left and falls off
 * to the "to" colour at the edges. The intermediate stops are straight sRGB mixes of the two CMS
 * colours — the same ramp the previously hard-coded maroon→black stops described, now driven by
 * `table_gradient_from` / `table_gradient_to`. Unparseable colours degrade to a plain two-stop
 * gradient so both fields still take effect.
 */
const TABLE_GRADIENT_STOPS = [0, 0.25, 0.5, 0.75, 0.94, 1];

/**
 * `value_font_size` is a free-text CMS field whose placeholder reads "e.g. 64px", so editors
 * legitimately type either "64" or "64px". Only append the unit when one isn't already there —
 * blindly appending produced "64pxpx", which the browser drops on the floor.
 */
const cssFontSize = (value: string | number | undefined | null): string | undefined => {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  return /^-?[\d.]+$/.test(raw) ? `${raw}px` : raw;
};

const buildTableGradient = (fromColor: string, toColor: string): string => {
  const from = parseColor(fromColor);
  const to = parseColor(toColor);
  const stops =
    from && to
      ? TABLE_GRADIENT_STOPS.map((stop) => `${mixColor(from, to, stop)} ${stop * 100}%`)
      : [`${fromColor} 0%`, `${toColor} 100%`];
  return `radial-gradient(120% 130% at 26% 30%, ${stops.join(", ")})`;
};

const parsePath = (c: any): string[] =>
  String(c?.tab_group || "")
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);

const ProgramFee2Variant3 = ({ data }: { data: { data?: any } | any }) => {
  const feeData = data?.data ?? data;
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);
  const prevTabRef = useRef<string | null>(null);

  const categories = useMemo(() => {
    if (!feeData?.categories) return [];
    return [...feeData.categories]
      .filter((c: any) => c?.status !== false)
      .sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [feeData?.categories]);

  const maxDepth = useMemo(
    () => categories.reduce((m: number, c: any) => Math.max(m, parsePath(c).length), 0),
    [categories],
  );

  const optionsAtLevel = (level: number, path: string[]): string[] => {
    const seen: string[] = [];
    for (const c of categories) {
      const p = parsePath(c);
      if (p.length <= level) continue;
      let ok = true;
      for (let i = 0; i < level; i++) if (p[i] !== path[i]) { ok = false; break; }
      if (ok && !seen.includes(p[level])) seen.push(p[level]);
    }
    return seen;
  };

  const innerCategories = (path: string[]): any[] =>
    categories.filter((c: any) => {
      const p = parsePath(c);
      if (p.length < maxDepth) return maxDepth === 0;
      for (let i = 0; i < maxDepth; i++) if (p[i] !== path[i]) return false;
      return true;
    });

  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [activeCatId, setActiveCatId] = useState<string | null>(null);

  // Resolve the effective path during render (default = first option at each level)
  // so the server render and the first client render match — avoids a hydration mismatch.
  const resolvedPath = useMemo(() => {
    const path: string[] = [];
    for (let l = 0; l < maxDepth; l++) {
      const opts = optionsAtLevel(l, path);
      if (opts.length === 0) break;
      path.push(selectedPath[l] && opts.includes(selectedPath[l]) ? selectedPath[l] : opts[0]);
    }
    return path;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, maxDepth, selectedPath]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) FireWidgetViewedEvent(ref, "program_fee_section");
  // }, []);

  const innerCats = useMemo(
    () => innerCategories(resolvedPath),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, maxDepth, resolvedPath],
  );
  const activeCategory = useMemo(
    () => innerCats.find((c: any) => c.id === activeCatId) ?? innerCats[0],
    [innerCats, activeCatId],
  );

  const selectLevel = (level: number, option: string) => {
    const newPath = resolvedPath.slice(0, level);
    newPath[level] = option;
    setSelectedPath(newPath);
    setActiveCatId(null); // fall back to the first inner tab of the newly selected path
    tabSwitched({ section: "program_fee_section", component: "fee_tabs", label: option });
  };

  const selectInnerTab = (catId: string, label: string) => {
    if (prevTabRef.current && prevTabRef.current !== catId) {
      tabSwitched({ section: "program_fee_section", component: "fee_pills", label });
    }
    prevTabRef.current = catId;
    setActiveCatId(catId);
  };

  const normalRows = useMemo(() => {
    if (!activeCategory?.metadata) return [];
    return activeCategory.metadata
      .filter((r: any) => r.status !== false && !r.is_total)
      .sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [activeCategory]);

  const totalRow = useMemo(
    () => activeCategory?.metadata?.find((r: any) => r.status !== false && r.is_total) ?? null,
    [activeCategory],
  );

  const benefits = useMemo(() => {
    if (!feeData?.metadata) return [];
    return [...feeData.metadata].filter((m: any) => m.status !== false).sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [feeData?.metadata]);

  const features = useMemo(() => {
    if (!feeData?.metadata_1) return [];
    return [...feeData.metadata_1].filter((m: any) => m.status !== false).sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [feeData?.metadata_1]);

  if (!feeData?.status || categories.length === 0) return null;

  const titleColor = themed(feeData.title_1_color, ["rgba(34,34,34,0.8)"], INK);
  const metaTitleColor = themed(feeData.metadata_title_color, ["#000000"], INK);
  const ctaBg = themed(feeData.cta_gradient_from, ["#02a7b6", "#342b7c"], MAROON);
  const ctaText = themed(feeData.cta_text_color, [], "#ffffff");
  const bgColor = themed(feeData.background_color, ["#000000"], "#ffffff");

  // Fee table container background, driven by both CMS gradient fields.
  const tableGradientFrom = themed(feeData.table_gradient_from, ["#342b7c"], MAROON);
  const tableGradientTo = themed(feeData.table_gradient_to, ["#02a7b6"], TABLE_GRADIENT_END);
  const tableBg = buildTableGradient(tableGradientFrom, tableGradientTo);

  const sectionGradient = buildSectionGradient(feeData.backvariant, [
    feeData.gradient_color_1,
    feeData.gradient_color_2,
    feeData.gradient_color_3,
    feeData.gradient_color_4,
  ]);

  const isAnimationEnabled =
    feeData.enable_animation === true ||
    feeData.enable_animation === 1 ||
    feeData.enable_animation === "1";

  const titleText = [feeData.title_1, feeData.title_2].filter(Boolean).join(" ");

  return (
    <section
      ref={ref}
      className={cn(
        "flex justify-center w-full",
        "px-[20px] py-[40px]",
        // Figma component reference: 80px horizontal padding from sm up; mobile keeps its 20px.
        "sm:px-[80px] sm:py-[60px]",
      )}
      style={{ backgroundColor: bgColor, backgroundImage: sectionGradient }}
    >
      <div className="flex flex-col w-full max-w-[1280px] gap-[40px] lg:gap-[52px]">
        {/* Title + outer tab levels */}
        <div className="flex flex-col items-center gap-[14px] w-full">
          {titleText && (
            <p
              className={cn(
                "text-center font-semibold [word-break:break-word] w-full leading-[1.25]",
                "text-[28px] md:text-[32px] lg:text-[36px] lg:tracking-[-0.54px] xl:text-[42px]",
              )}
              style={{ color: titleColor, fontFamily: FONT_POPPINS }}
            >
              {titleText}
            </p>
          )}

          {/* Outer tab levels (underline style), one row per nesting level */}
          {Array.from({ length: maxDepth }).map((_, level) => {
            const opts = optionsAtLevel(level, resolvedPath.slice(0, level));
            if (opts.length === 0) return null;
            return (
              <div
                key={level}
                className="flex gap-[12px] items-center border-b-[0.6px] border-[rgba(0,0,0,0.2)] overflow-x-auto scrollbar-hide max-w-full"
              >
                {opts.map((opt, indx) => {
                  const isActive = resolvedPath[level] === opt;
                  return (
                    // `shrink-0` moves onto the reveal wrapper, which becomes the flex item.
                    <RevealOnScroll
                      key={opt}
                      className="shrink-0"
                      y={0}
                      x={-100 * (indx + 1)}
                      delay={indx * 100}
                      enableAnimation={isAnimationEnabled}
                    >
                      <button
                        onClick={() => selectLevel(level, opt)}
                        className={cn(
                          "shrink-0 flex items-center justify-center px-[16px] py-[10px] whitespace-nowrap transition-colors",
                          "text-[15px] sm:text-[16px]",
                          isActive ? "font-semibold border-b-4" : "font-semibold",
                        )}
                        style={{
                          fontFamily: isActive ? FONT_POPPINS : FONT_VISUAL,
                          color: isActive ? MAROON : "#000000",
                          borderColor: isActive ? MAROON : "transparent",
                        }}
                      >
                        {opt}
                      </button>
                    </RevealOnScroll>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Main: fee table + What You Get */}
        <div className="flex flex-col gap-[40px] sm:flex-row sm:gap-[50px] sm:items-start w-full">
          {/* Fee table column */}
          <div className="flex flex-col gap-[24px] sm:gap-[20px] w-full sm:w-[530px] md:w-[612px] lg:w-[693px] shrink-0">
            {/* Inner pills (category tabs) */}
            {innerCats.length > 1 && (
              <div className="flex gap-[12px] items-center overflow-x-auto scrollbar-hide">
                {innerCats.map((cat: any, indx: number) => {
                  const isActive = cat.id === (activeCategory?.id ?? null);
                  return (
                    <RevealOnScroll
                      key={cat.id}
                      className="shrink-0"
                      y={0}
                      x={-100 * (indx + 1)}
                      delay={indx * 100}
                      enableAnimation={isAnimationEnabled}
                    >
                      <button
                        onClick={() => selectInnerTab(cat.id, cat.title)}
                        className={cn(
                          "shrink-0 flex items-center justify-center px-[16px] py-[10px] whitespace-nowrap transition-colors",
                          "text-[15px] sm:text-[16px]",
                          isActive ? "font-semibold rounded-[40px]" : "font-semibold rounded-[70px]",
                        )}
                        style={{
                          fontFamily: isActive ? FONT_POPPINS : FONT_VISUAL,
                          backgroundColor: isActive ? themed(cat.active_bg_color, ["#02a7b6"], MAROON) : "transparent",
                          color: isActive ? (cat.active_text_color || "#ffffff") : "#000000",
                          border: isActive
                            ? `1px solid ${themed(cat.active_bg_color, ["#02a7b6"], MAROON)}`
                            : "0.6px solid rgba(0,0,0,0.4)",
                        }}
                      >
                        {cat.title}
                      </button>
                    </RevealOnScroll>
                  );
                })}
              </div>
            )}

            {/* Fee breakdown card */}
            <div className="flex flex-col gap-[2px] p-[10px] sm:p-[20px] w-full text-white" style={{ backgroundImage: tableBg }}>
              {normalRows.map((row: any, indx: number) => (
                <RevealOnScroll
                  key={row.id}
                  y={-100}
                  delay={indx * 100}
                  enableAnimation={isAnimationEnabled}
                >
                  <div className="flex items-center justify-center gap-[20px] p-[8px] sm:px-[24px] sm:py-[16px]">
                    <p
                      className="flex-1 min-w-px leading-[1.65] text-[14px] sm:text-[17px] font-medium"
                      style={{ fontFamily: FONT_POPPINS, color: row.text_color || undefined }}
                    >
                      {row.label}
                    </p>
                    <p
                      className="shrink-0 text-center whitespace-nowrap leading-[1.6] text-[14px] sm:text-[20px] font-normal"
                      style={{
                        fontFamily: FONT_POPPINS,
                        color: row.value_text_color || undefined,
                        fontSize: cssFontSize(row.value_font_size),
                      }}
                    >
                      {row.value}
                    </p>
                  </div>
                </RevealOnScroll>
              ))}

              {totalRow && (
                <RevealOnScroll
                  y={-500}
                  delay={Math.min(normalRows.length * 300, 700)}
                  enableAnimation={isAnimationEnabled}
                >
                  <div className="flex items-center justify-center gap-[20px] p-[8px] sm:px-[24px] sm:py-[16px] border-t-[0.6px] border-white">
                    <p
                      className="flex-1 min-w-px leading-[1.65] text-[14px] sm:text-[17px] font-medium"
                      style={{ fontFamily: FONT_POPPINS, color: totalRow.text_color || undefined }}
                    >
                      {totalRow.label}
                    </p>
                    <p
                      className="shrink-0 text-right whitespace-nowrap font-semibold leading-[1.15] text-[28px] tracking-[-0.56px] sm:text-[48px] sm:tracking-[-0.96px]"
                      style={{
                        fontFamily: FONT_POPPINS,
                        color: totalRow.value_text_color || undefined,
                        fontSize: cssFontSize(totalRow.value_font_size),
                      }}
                    >
                      {totalRow.value}
                    </p>
                  </div>
                </RevealOnScroll>
              )}
            </div>
          </div>

          {/* What You Get */}
          {benefits.length > 0 && (
            <div className="flex flex-col gap-[16px] lg:gap-[13px] flex-1 min-w-0">
              {feeData.metadata_title && (
                <RevealOnScroll y={0} x={0} duration={1200} enableAnimation={isAnimationEnabled}>
                  <p className="font-semibold leading-[1.5] text-[18px] w-full" style={{ fontFamily: FONT_POPPINS, color: metaTitleColor }}>
                    {feeData.metadata_title}
                  </p>
                </RevealOnScroll>
              )}
              <div className="flex flex-col w-full">
                {/* Figma uses a plain "✓" glyph; an uploaded icon replaces it when the CMS supplies one. */}
                {benefits.map((benefit: any, indx: number) => (
                  <RevealOnScroll
                    key={benefit.id}
                    y={50}
                    duration={800}
                    delay={indx * 100}
                    enableAnimation={isAnimationEnabled}
                  >
                  <div className="flex items-start gap-[8px] mb-[8px] sm:mb-0">
                    {benefit.icon && (
                      <span className="relative shrink-0 mt-[3px] size-[18px] sm:size-[20px]">
                        <Image
                          src={benefit.icon}
                          alt={benefit.icon_alt || benefit.label}
                          fill
                          className="object-contain"
                        />
                      </span>
                    )}
                    <p
                      className="leading-[1.4] sm:leading-[1.65] text-[14px] sm:text-[17px] font-medium"
                      style={{ fontFamily: FONT_POPPINS, color: benefit.text_color || MUTED }}
                    >
                      {benefit.icon ? benefit.label : `✓ ${benefit.label}`}
                    </p>
                  </div>
                  </RevealOnScroll>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom features */}
        {features.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-[24px] sm:gap-[40px] lg:gap-[80px] sm:items-center w-full">
            {features.map((feature: any, indx: number) => (
              // Sizing classes sit on BOTH the wrapper and the row: with animation on the wrapper
              // is the flex item, and with it off RevealOnScroll renders children bare.
              <RevealOnScroll
                key={feature.id}
                className="flex-1 min-w-0 w-full"
                y={0}
                x={-100 * (indx + 1)}
                delay={indx * 100}
                enableAnimation={isAnimationEnabled}
              >
                <div className="flex items-center gap-[20px] flex-1 min-w-0 w-full">
                  {feature.icon && (
                    <div className="relative shrink-0 size-[30px] sm:size-[40px]">
                      <Image src={feature.icon} alt={feature.icon_alt || feature.label} fill className="object-contain" />
                    </div>
                  )}
                  <p className="flex-1 min-w-0" style={{ fontFamily: FONT_POPPINS }}>
                    <span className="font-semibold leading-[1.5] text-[16px] sm:text-[18px]" style={{ color: feature.text_color || INK }}>
                      {feature.label}
                    </span>
                    {feature.description && (
                      <span className="block font-medium leading-[1.65] text-[14px] sm:text-[17px]" style={{ color: feature.value_text_color || MUTED }}>
                        {feature.description}
                      </span>
                    )}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        )}

        {/* CTA */}
        {feeData.cta_text && (
          <RevealOnScroll y={50} enableAnimation={isAnimationEnabled}>
          <div className="flex justify-center w-full">
            <button
              onClick={() => {
                leadCtaClick({ section: "program_fee_section", component: "cta_button", label: feeData.cta_text });
                dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
              }}
              className="flex items-center justify-center gap-[10px] w-full sm:w-auto px-[24px] py-[16px] rounded-[8px] font-semibold leading-[1.4] text-[16px] cursor-pointer transition-transform"
              style={{
                fontFamily: FONT_POPPINS,
                color: ctaText,
                backgroundColor: ctaBg,
                boxShadow: "0px 10px 10px rgba(202,125,139,0.6)",
              }}
            >
              <span className="whitespace-nowrap">{feeData.cta_text}</span>
              {feeData.cta_icon ? (
                <span className="relative inline-block shrink-0 size-[24px]">
                  <Image src={feeData.cta_icon} alt={feeData.cta_icon_alt || ""} fill className="object-contain" />
                </span>
              ) : (
                <svg className="size-[24px] shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.5 7.5L15.5 7.5L15.5 14.5M15 8L7 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

export default ProgramFee2Variant3;
